import { useCallback, useEffect, useRef, useState } from 'react';

export type ClipboardInput = string | Record<string, string>;

type UseCopyToClipboardResult = {
  copied: boolean;
  copy: (input: ClipboardInput) => Promise<boolean>;
};

const writeToClipboard = async (input: ClipboardInput): Promise<boolean> => {
  try {
    if (typeof input === 'string') {
      await navigator.clipboard.writeText(input);
      return true;
    }

    if (typeof ClipboardItem !== 'undefined') {
      const items = Object.fromEntries(
        Object.entries(input).map(([mime, content]) => [
          mime,
          new Blob([content], { type: mime }),
        ]),
      );
      await navigator.clipboard.write([new ClipboardItem(items)]);
      return true;
    }

    const plainText = input['text/plain'];
    if (plainText === undefined) {
      return false;
    }
    await navigator.clipboard.writeText(plainText);
    return true;
  } catch {
    return false;
  }
};

export const useCopyToClipboard = (
  resetMs = 1500,
): UseCopyToClipboardResult => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const copy = useCallback(
    async (input: ClipboardInput): Promise<boolean> => {
      const success = await writeToClipboard(input);
      if (success) {
        setCopied(true);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => setCopied(false), resetMs);
      } else {
        setCopied(false);
      }
      return success;
    },
    [resetMs],
  );

  return { copied, copy };
};
